import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { attachRequest } from '../../../../api'
import { useAsyncCallback, useNotification } from '../../../../hooks'
import { AttachServiceRequestsModal, useCreateRequest } from '../../../Request'
import { ChatroomMessageTypeModal } from '../../ChatroomMessageActions'
import { useChatroomMessageDelete } from '../useChatroomMessageDelete'

import {
  getChatroomMessageAttachRequestExcludeIds,
  getChatroomMessageCreateRequestAttachPayload,
  getChatroomMessageCreateRequestInitialValues,
} from './useChatroomMessageActions.utils'

export function useChatroomMessageActions({
  message,
  outboundUuid,
  onEditMessage,
  onRefetchVisibleMessages,
  onUpdateMessage,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [modal, setModal] = useState(null)

  const handleCloseModal = useCallback(() => {
    setModal(null)
  }, [])

  const { handleOpenCreation, requestCreationModal } = useCreateRequest({
    setModal,
    onClose: handleCloseModal,
    onReload: onRefetchVisibleMessages,
  })

  const { deleteConfirmationModal, handleClickDelete } =
    useChatroomMessageDelete({ message, outboundUuid, onUpdateMessage })

  const openCreateRequestForChat = useCallback(
    chosenIsPublic => {
      handleOpenCreation({
        formProps: {
          initialValues: getChatroomMessageCreateRequestInitialValues({
            isChatroomPublic: chosenIsPublic,
            message,
          }),
        },
        wizardProps: {
          chatMessageUuid: message.uuid,
          isChatroomPublic: chosenIsPublic,
        },
      })
    },
    [handleOpenCreation, message]
  )

  const handleAttachToRequest = useAsyncCallback(
    async ({ id, isChatroomPublic }) => {
      try {
        const response = await attachRequest(
          id,
          getChatroomMessageCreateRequestAttachPayload({
            isChatroomPublic,
            message,
            requestId: id,
          })
        )

        if (response) {
          handleAddSuccessNotification(
            t('Chatroom.MessageAttachToRequestSuccessKey')
          )

          onRefetchVisibleMessages?.()
        }
      } catch {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      } finally {
        handleCloseModal()
      }
    },
    [
      handleAddErrorNotification,
      handleAddSuccessNotification,
      handleCloseModal,
      message,
      onRefetchVisibleMessages,
      t,
    ]
  )

  const openAttachRequestForChat = useCallback(
    chosenIsPublic => {
      setModal(
        <AttachServiceRequestsModal
          canSelectOnlyOneNotificationKey='Modal.ChatroomMessageAttachServiceRequestCanSelectOnlyOneKey'
          entityId={message.uuid}
          excludeIds={getChatroomMessageAttachRequestExcludeIds(message)}
          onAttach={({ id }) =>
            handleAttachToRequest({ id, isChatroomPublic: chosenIsPublic })
          }
          onClose={handleCloseModal}
        />
      )
    },
    [handleAttachToRequest, handleCloseModal, message]
  )

  const openChatroomTypeModal = useCallback(
    onPick => {
      setModal(
        <ChatroomMessageTypeModal
          onClose={handleCloseModal}
          onSubmit={chosenIsPublic => {
            onPick(chosenIsPublic)
          }}
        />
      )
    },
    [handleCloseModal]
  )

  const handleClickCreateRequest = useCallback(() => {
    openChatroomTypeModal(openCreateRequestForChat)
  }, [openChatroomTypeModal, openCreateRequestForChat])

  const handleClickAttachRequest = useCallback(() => {
    openChatroomTypeModal(openAttachRequestForChat)
  }, [openAttachRequestForChat, openChatroomTypeModal])

  function handleClickEdit() {
    onEditMessage?.(message.uuid)
  }

  return {
    handleClickAttachRequest,
    handleClickCreateRequest,
    handleClickDelete,
    handleClickEdit,
    modal: modal || requestCreationModal || deleteConfirmationModal,
  }
}
